/**
 * API Service functions for Student Profile
 */
import Service from '../../services';
import { baseUrlAdmin } from '../../store/apiConstants';
import { transformStudentData } from './dataMapper';
import { transformAttendanceApiResponse, transformFlatAttendanceData } from './attendanceHelpers';
import { calculateTotalsFromMonths } from './utils';

/**
 * Fetch student profile data
 * @param {string} studentId - Student ID
 * @param {string} academicYear - Academic Year
 * @param {Object} toast - Toast reference for notifications
 * @param {Function} setState - setState function from component
 * @param {Function} fetchAttendanceData - Function to fetch attendance data
 * @returns {Promise<Object>} - Transformed student data or null
 */
export const fetchStudentData = async (studentId, academicYear, toast, setState, fetchAttendanceData) => {
    let payload = { user_id: studentId, academicYear: academicYear };
    let url = `${baseUrlAdmin}/student-profile`;

    setState({ isLoading: true });

    const service = new Service();

    try {
        // Fetch student profile data
        const profileResponse = await service.post(url, payload, true);

        if (profileResponse && profileResponse.status && profileResponse.res.status) {
            // The API response has the complete data at res.res.data level
            const apiData = profileResponse.res.data || profileResponse.res;

            // Transform API response to match UI data structure
            const transformedData = transformStudentData(apiData);

            // Fetch attendance data
            await fetchAttendanceData(studentId, academicYear);

            // Ensure we always have attendance data
            setState((prevState) => ({
                isLoading: false,
                studentData: transformedData,
                showDetailView: true,
                activeDetailView: 'personalInfo',
                activeMenu: 'studentDetails',
                activeSubMenu: 'personalInfo',
                studentId: studentId, // Store studentId for refresh operations
                // Keep existing attendance data if already loaded
                attendanceData: prevState.attendanceData && Object.keys(prevState.attendanceData).length > 0
                    ? prevState.attendanceData
                    : {},
                // Reset all finance-related states when student changes
                showFinanceView: false,
                showStudentDetailsView: true, // Ensure student details panel is visible
                activeFinanceView: 'receiptEntry',
                financeMinimized: false,
                activeFeeDetailsTab: 'feetypeWise',
                feetypeWiseFeeData: null,
                feetypeWiseFeeLoading: false,
                feeTermsRawData: null,
                feeHistoryData: null,
                feeHistoryLoading: false,
                transactionsData: null,
                transactionsLoading: false
            }));

            if (toast.current) {
                toast.current.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Student profile loaded successfully',
                    life: 3000
                });
            }

            return transformedData;
        } else {
            setState({ isLoading: false });
            if (toast.current) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: profileResponse.errMessage || 'Failed to fetch student data',
                    life: 3000
                });
            }
            return null;
        }
    } catch (e) {
        setState({ isLoading: false });
        if (toast.current) {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: e.message || 'An error occurred',
                life: 3000
            });
        }
        return null;
    }
}

/**
 * Fetch attendance data
 * @param {string} studentId - Student ID
 * @param {string} academicYear - Academic Year
 * @param {Object} toast - Toast reference for notifications
 * @param {Function} setState - setState function from component
 * @returns {Promise<void>}
 */
export const fetchAttendanceData = async (studentId, academicYear, toast, setState) => {
    const payload = {
        user_id: studentId,
        academicYear: academicYear
    };
    const attendanceUrl = `${baseUrlAdmin}/student-profile/attendance`;

    const service = new Service();

    try {
        const attendanceResponse = await service.post(attendanceUrl, payload, true);

        if (attendanceResponse && attendanceResponse.status && attendanceResponse.res) {
            // Transform the API response to match our expected format
            const transformedAttendanceData = transformAttendanceApiResponse(attendanceResponse.res);

            if (transformedAttendanceData && Object.keys(transformedAttendanceData).length > 0) {
                setState({
                    attendanceData: transformedAttendanceData
                });

                if (toast.current) {
                    toast.current.show({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'Attendance data loaded from API',
                        life: 3000
                    });
                }
            } else {
                setState({
                    attendanceData: {}
                });
            }
        } else {
            setState({
                attendanceData: {}
            });
        }
    } catch (error) {
        // API failed, no attendance data available
        setState({
            attendanceData: {}
        });

        if (toast.current) {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Could not load attendance data from API',
                life: 4000
            });
        }
    }
}

/**
 * Export attendance to Excel
 * @param {Object} state - Component state containing attendanceData, activeAttendanceTab, studentData
 * @param {Object} toast - Toast reference for notifications
 * @returns {void}
 */
export const exportAttendanceToExcel = (state, toast) => {
    const { attendanceData, activeAttendanceTab, studentData } = state;

    if (!attendanceData || !attendanceData[activeAttendanceTab]) {
        toast.current?.show({
            severity: 'warn',
            summary: 'No Data',
            detail: 'No attendance data available to export'
        });
        return;
    }

    const currentAttendanceData = attendanceData[activeAttendanceTab];
    const allMonthsData = currentAttendanceData.months || [];
    const totals = currentAttendanceData.totals || {};

    // Format data for export using the same pattern as reports
    const exportData = allMonthsData.map(monthData => ({
        'Month': monthData?.month || '',
        'Days': monthData?.noOfDays || 0,
        'Week Offs': monthData?.weekoffs || 0,
        'Holidays': monthData?.holidays || 0,
        'Working Days': monthData?.workingDays || 0,
        'Present': monthData?.present || 0,
        'Absent': monthData?.absent || 0,
        'Half Day': monthData?.halfday || 0,
        'Percentage': monthData?.percentage ? `${monthData.percentage}%` : '0%'
    }));

    // Add totals row
    exportData.push({
        'Month': 'Total',
        'Days': totals.noOfDays || 0,
        'Week Offs': totals.weekoffs || 0,
        'Holidays': totals.holidays || 0,
        'Working Days': totals.workingDays || 0,
        'Present': totals.present || 0,
        'Absent': totals.absent || 0,
        'Half Day': totals.halfday || 0,
        'Percentage': totals.percentage ? `${totals.percentage}%` : '0%'
    });

    // Use dynamic import like working reports
    import('xlsx').then(XLSX => {
        // Create worksheet and workbook
        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();

        // Set column widths
        worksheet['!cols'] = [
            { width: 15 }, // Month
            { width: 8 },  // Days
            { width: 12 }, // Week Offs
            { width: 10 }, // Holidays
            { width: 15 }, // Working Days
            { width: 10 }, // Present
            { width: 10 }, // Absent
            { width: 12 }, // Half Day
            { width: 12 }  // Percentage
        ];

        const studentName = studentData?.studentName || 'Student';
        const sheetName = `${activeAttendanceTab.charAt(0).toUpperCase() + activeAttendanceTab.slice(1)} Attendance`;
        XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

        // Generate filename with current date
        const currentDate = new Date().toISOString().split('T')[0].replace(/-/g, '-');
        const filename = `${studentName.replace(/[^a-zA-Z0-9]/g, '_')}_${activeAttendanceTab}_Attendance_${currentDate}.xlsx`;

        // Save file
        XLSX.writeFile(workbook, filename);

        // Show success message
        toast.current?.show({
            severity: 'success',
            summary: 'Export Successful',
            detail: 'Attendance data exported to Excel successfully'
        });
    }).catch(() => {
        toast.current?.show({
            severity: 'error',
            summary: 'Export Failed',
            detail: 'Failed to export attendance data to Excel'
        });
    });
}

/**
 * Fetch feetype terms data
 * @param {string} studentId - Student ID
 * @param {string} academicYear - Academic Year
 * @returns {Promise<Object>} - API response data or null
 */
export const fetchFeetypeTerms = async (studentId, academicYear) => {
    const url = `${baseUrlAdmin}/concession/get-feetype-terms/${studentId}?academicYear=${academicYear}`;
    const service = new Service();

    try {
        const response = await service.get(url, true);

        if (response && response.status && response.res && response.res.data) {
            return response.res.data;
        } else {
            return null;
        }
    } catch (error) {
        return null;
    }
}

/**
 * Fetch fee history data
 * @param {string} studentId - Student ID
 * @param {string} academicYear - Academic Year
 * @param {Object} toast - Toast reference for notifications
 * @returns {Promise<Object>} - Fee history data or null
 */
export const fetchFeeHistory = async (studentId, academicYear, toast) => {
    const payload = {
        user_id: studentId,
        academicYear: academicYear
    };
    const url = `${baseUrlAdmin}/student-profile/fee-history`;

    const service = new Service();

    try {
        const response = await service.post(url, payload, true);

        if (response && response.status && response.res && response.res.data) {
            const data = response.res.data;
            
            if (toast && toast.current) {
                toast.current.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Fee history loaded successfully',
                    life: 3000
                });
            }
            return data;
        } else {
            if (toast && toast.current) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to fetch fee history data',
                    life: 3000
                });
            }
            return null;
        }
    } catch (error) {
        if (toast && toast.current) {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: error.message || 'An error occurred while fetching fee history',
                life: 3000
            });
        }
        return null;
    }
}

/**
 * Fetch transactions/fee collections data
 * @param {string} studentId - Student ID (user_id)
 * @param {string} academicYear - Academic Year
 * @param {Object} toast - Toast reference for notifications
 * @returns {Promise<Object>} - Transactions data or null
 */
export const fetchTransactions = async (studentId, academicYear, toast) => {
    const payload = {
        user_id: studentId,
        academicYear: academicYear
    };
    const url = `${baseUrlAdmin}/student-profile/fee-collections`;

    const service = new Service();

    try {
        const response = await service.post(url, payload, true);

        if (response && response.status && response.res && response.res.data) {
            const data = response.res.data;
            
            // Success - no toast notification to avoid spam when navigating finance menu
            return data;
        } else {
            if (toast && toast.current) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to fetch transactions data',
                    life: 3000
                });
            }
            return null;
        }
    } catch (error) {
        if (toast && toast.current) {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: error.message || 'An error occurred while fetching transactions',
                life: 3000
            });
        }
        return null;
    }
}

/**
 * Fetch transport fee data (filtered for transport fee types only)
 * @param {string} studentId - Student ID (user_id)
 * @param {string} academicYear - Academic Year
 * @param {Object} toast - Toast reference for notifications
 * @returns {Promise<Array>} - Filtered and sorted transport fee data or empty array
 */
/**
 * Fetch cancelled transactions data
 * @param {string} studentId - Student ID (user_id)
 * @param {string} academicYear - Academic Year
 * @param {Object} toast - Toast reference for notifications (optional)
 * @returns {Promise<Object|null>} - Cancelled transactions data or null
 */
export const fetchCancelledTransactions = async (studentId, academicYear, toast) => {
    const payload = {
        user_id: studentId,
        academicYear: academicYear
    };
    const url = `${baseUrlAdmin}/student-profile/cancelled-transactions`;
    console.log('fetchCancelledTransactions - URL:', url);
    console.log('fetchCancelledTransactions - Payload:', payload);

    const service = new Service();

    try {
        console.log('Making API call to fetch cancelled transactions...');
        const response = await service.post(url, payload, true);
        console.log('API response:', response);

        if (response && response.status && response.res && response.res.data) {
            const data = response.res.data;
            return data;
        } else {
            if (toast && toast.current) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to fetch cancelled transactions data',
                    life: 3000
                });
            }
            return null;
        }
    } catch (error) {
        if (toast && toast.current) {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: error.message || 'An error occurred while fetching cancelled transactions',
                life: 3000
            });
        }
        return null;
    }
}

/**
 * Export daily attendance to Excel
 * @param {Object} state - Component state containing dailyAttendanceData, selectedMonth, selectedAttendanceType, studentData
 * @param {Object} toast - Toast reference for notifications
 * @returns {void}
 */
export const exportDailyAttendanceToExcel = (state, toast) => {
    const { dailyAttendanceData, selectedMonth, selectedAttendanceType, studentData } = state;

    if (!dailyAttendanceData || dailyAttendanceData.length === 0) {
        toast.current?.show({
            severity: 'warn',
            summary: 'No Data',
            detail: 'No daily attendance data available to export'
        });
        return;
    }

    // Format data for export
    const exportData = dailyAttendanceData.map(dayData => ({
        'Date': dayData.date,
        'Day': dayData.day,
        'Status': dayData.status || ''
    }));

    // Use dynamic import like working reports
    import('xlsx').then(XLSX => {
        // Create worksheet and workbook
        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();

        const studentName = studentData?.studentName || 'Student';
        const sheetName = `Daily Attendance - ${selectedMonth}`;
        XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

        // Generate filename with current date
        const currentDate = new Date().toISOString().split('T')[0].replace(/-/g, '-');
        const filename = `${studentName.replace(/[^a-zA-Z0-9]/g, '_')}_Daily_Attendance_${selectedMonth}_${currentDate}.xlsx`;

        // Save file
        XLSX.writeFile(workbook, filename);

        // Show success message
        toast.current?.show({
            severity: 'success',
            summary: 'Export Successful',
            detail: 'Daily attendance data exported to Excel successfully'
        });
    }).catch(() => {
        toast.current?.show({
            severity: 'error',
            summary: 'Export Failed',
            detail: 'Failed to export daily attendance data to Excel'
        });
    });
}
